import { useRef, useState } from 'react';

import { namedOptions, toGroupFields, toGroupPayload, visibleModifiers } from './Modifiers.formState';
import { planItemLinks, planOptionChanges } from './Modifiers.savePlan';
import useModifierMutations from './useModifierMutations';

const reconcileSavedGroup = (group, optionPlan, createdModifiers) => {
  const updatesById = new Map(optionPlan.updated.map(change => [change.modifierId, change.updates]));
  const removedIds = new Set(optionPlan.removed);

  const kept = (group?.modifiers || [])
    .filter(modifier => !removedIds.has(modifier.core?.id))
    .map(modifier => {
      const updates = updatesById.get(modifier.core?.id);
      if (!updates) return modifier;
      const { enabled, soldOut, ...core } = updates;
      return { ...modifier, enabled, soldOut, core: { ...modifier.core, ...core } };
    });

  return { ...group, modifiers: [...kept, ...createdModifiers.filter(Boolean)] };
};

export default function useModifierGroupSave({ venueId, group, adoptSavedOption, onSaved }) {
  const [saveFailed, setSaveFailed] = useState(false);
  const [savedGroup, setSavedGroup] = useState(null);
  const savingRef = useRef(false);

  const {
    createGroup,
    createModifier,
    linkGroupToItems,
    refreshGroups,
    removeModifier,
    unlinkGroupFromItem,
    updateGroup,
    updateModifier,
  } = useModifierMutations({ venueId });

  const workingGroup = savedGroup || group;

  const saveGroup = async formValues => {
    const groupId = workingGroup?.core?.id;
    if (!groupId) {
      const created = await createGroup.mutateAsync(toGroupPayload(formValues));
      setSavedGroup(created);
      // The server returns the options in the order they were sent, which is the order of the
      // named rows; adopting by client id keeps a blank row among them from taking someone's id.
      const sent = namedOptions(formValues);
      visibleModifiers(created).forEach((modifier, position) =>
        adoptSavedOption(sent[position]?.core?.id, modifier.core.id)
      );
      return created?.core?.id;
    }

    await updateGroup.mutateAsync({ groupId, group: toGroupFields(formValues) });
    const optionPlan = planOptionChanges(workingGroup, formValues);
    const [created] = await Promise.all([
      Promise.all(
        optionPlan.created.map(({ clientId, modifier }) =>
          createModifier.mutateAsync({ groupId, modifier }).then(saved => {
            adoptSavedOption(clientId, saved?.core?.id);
            return saved;
          })
        )
      ),
      Promise.all(optionPlan.updated.map(change => updateModifier.mutateAsync(change))),
      Promise.all(optionPlan.removed.map(modifierId => removeModifier.mutateAsync({ modifierId }))),
    ]);

    setSavedGroup(previous => reconcileSavedGroup(previous || workingGroup, optionPlan, created));
    return groupId;
  };

  const save = async formValues => {
    // Disabling the button only takes effect on the next render, so a fast double-click gets two
    // submits through and creates the group twice.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaveFailed(false);

    try {
      const groupId = await saveGroup(formValues);
      const { linked, unlinked } = planItemLinks(workingGroup, formValues);

      if (linked.length > 0) await linkGroupToItems.mutateAsync({ groupId, itemIds: linked });
      await Promise.all(unlinked.map(itemId => unlinkGroupFromItem.mutateAsync({ groupId, itemId })));

      refreshGroups();
      onSaved(groupId);
    } catch (error) {
      setSaveFailed(true);
    } finally {
      savingRef.current = false;
    }
  };

  return { save, saveFailed };
}
